/**
 * CR130 Phase 3: Shared Deal Details Content Component
 * CR151: Added DealProofSection for admin debug info
 * 
 * Single source of truth for deal details content rendered in all modes:
 * - Flip (back of card)
 * - Expand (accordion below card)
 * - Panel (side inspector)
 * 
 * Renders enabled sections in configured order with mode-specific clamping.
 */

import { useMemo } from "react";
import { ExternalLink, FileText, Clock, Eye, ShieldCheck, Sparkles, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import type { Deal } from "@/lib/types/deals";
import type { DetailsMode, DetailsSectionConfig, DetailsSectionKey } from "@/lib/layout-studio/dealCardUXTypes";
import { formatValidity, formatBonusValidity, formatWhereFound } from "@/lib/types/deals";
import { cn } from "@/lib/utils";
import { DealProofSection } from "./DealProofSection";
import { FlyerEvidenceSection } from "./FlyerEvidenceSection";
import { FlyerPreview } from "./FlyerPreview";
import type { FlyerArtifact } from "@/hooks/useAdminPreviewDeals";

// ================================================================
// PROPS
// ================================================================

interface DealDetailsContentProps {
  deal: Deal & { id?: string; flyer_artifact?: FlyerArtifact | null; show_flyer_evidence?: boolean };
  sectionsConfig: DetailsSectionConfig[];
  mode: DetailsMode;
  isAdmin?: boolean;
  className?: string;
  // CR151: Layout proof context
  layoutPresetName?: string | null;
  layoutEnvironment?: "staging" | "publish" | null;
  snapshotId?: string | null;
}

// ================================================================
// SECTION RENDERERS
// ================================================================

function TermsSection({ 
  deal, 
  maxLines,
  mode,
}: { 
  deal: Deal; 
  maxLines?: number;
  mode: DetailsMode;
}) {
  const clampClass = mode === "flip" && maxLines 
    ? `line-clamp-${Math.min(maxLines, 6)}` 
    : "";
  
  const terms = deal.raw_source_notes || deal.fine_print;
  
  if (!terms) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No terms available
      </div>
    );
  }
  
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
        <FileText className="w-4 h-4" />
        Offer Terms
      </h4>
      <p className={cn("text-sm text-muted-foreground", clampClass)}>
        {terms}
      </p>
    </div>
  );
}

function FinePrintSection({ 
  deal, 
  maxLines,
  mode,
}: { 
  deal: Deal; 
  maxLines?: number;
  mode: DetailsMode;
}) {
  const clampClass = mode === "flip" && maxLines 
    ? `line-clamp-${Math.min(maxLines, 6)}` 
    : "";
  
  const finePrint = deal.fine_print || deal.exclusions;
  
  if (!finePrint) {
    return null; // Hide section if no fine print
  }
  
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
        <Info className="w-4 h-4" />
        Fine Print / Exclusions
      </h4>
      <p className={cn("text-sm text-muted-foreground", clampClass)}>
        {finePrint}
      </p>
    </div>
  );
}

function SourceSection({ deal }: { deal: Deal }) {
  const validity = formatValidity(deal);
  const whereFound = formatWhereFound(deal);
  
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
        <Clock className="w-4 h-4" />
        Source & Validity
      </h4>
      <div className="space-y-1 text-sm text-muted-foreground">
        {validity && <p>{validity}</p>}
        {whereFound && <p className="text-xs">{whereFound}</p>}
        {deal.source_url && (
          <a 
            href={deal.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            View source <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}

function EvidenceSection({ deal }: { deal: Deal }) {
  if (!deal.evidence_snippet && !deal.decision_reasons?.length) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No evidence data available
      </div>
    );
  }
  
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
        <Eye className="w-4 h-4" />
        Evidence
      </h4>
      <div className="space-y-2 text-sm text-muted-foreground">
        {deal.evidence_snippet && (
          <p className="whitespace-pre-wrap text-xs bg-muted/50 p-2 rounded border">
            {deal.evidence_snippet}
          </p>
        )}
        {deal.decision_reasons && deal.decision_reasons.length > 0 && (
          <ul className="list-disc list-inside space-y-0.5 text-xs">
            {deal.decision_reasons.map((reason, idx) => (
              <li key={idx}>{reason}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ValidatorSection({ deal }: { deal: Deal }) {
  const flags = deal.validation_flags || [];
  
  if (flags.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-success" />
        No validation issues
      </div>
    );
  }
  
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4" />
        Validator Results
      </h4>
      <div className="flex flex-wrap gap-1">
        {flags.map((flag, idx) => (
          <Badge key={idx} variant="secondary" className="text-xs">
            {flag}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function ActionsSection({ deal }: { deal: Deal }) {
  const hasProof = deal.proof_urls && deal.proof_urls.length > 0;
  const hasTerms = deal.terms_url || deal.source_url;
  
  // CR287: Claim URL fallback chain: claim_url → deal_url → source_url
  const claimUrl = deal.claim_url || deal.deal_url || deal.source_url;
  const hasClaim = Boolean(claimUrl);
  
  if (!hasProof && !hasTerms && !hasClaim) {
    return null;
  }
  
  return (
    <div>
      <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
        <Sparkles className="w-4 h-4" />
        Actions
      </h4>
      <div className="flex flex-wrap gap-2">
        {deal.proof_urls?.[0] && (
          <a
            href={deal.proof_urls[0]}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            View Deal <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        )}
        {hasClaim ? (
          <a
            href={claimUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Claim Deal <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        ) : (
          <span className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 bg-muted text-muted-foreground cursor-not-allowed" title="No claim link available">
            Claim Deal
          </span>
        )}
        {hasTerms && !hasClaim && (
          <a
            href={deal.terms_url || deal.source_url!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <FileText className="w-3 h-3 mr-1" />
            T&Cs
          </a>
        )}
      </div>
    </div>
  );
}

// ================================================================
// SECTION FACTORY
// ================================================================

function renderSection(
  key: DetailsSectionKey,
  deal: Deal,
  config: DetailsSectionConfig,
  mode: DetailsMode,
  isAdmin: boolean,
): React.ReactNode {
  switch (key) {
    case "terms":
      return <TermsSection deal={deal} maxLines={config.flipMaxLines} mode={mode} />;
    case "fine_print":
      return <FinePrintSection deal={deal} maxLines={config.flipMaxLines} mode={mode} />;
    case "source":
      return <SourceSection deal={deal} />;
    case "evidence":
      // Evidence is admin-only by default
      return isAdmin ? <EvidenceSection deal={deal} /> : null;
    case "validator":
      // Validator is admin-only by default
      return isAdmin ? <ValidatorSection deal={deal} /> : null;
    case "actions":
      return <ActionsSection deal={deal} />;
    default:
      return null;
  }
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export function DealDetailsContent({
  deal,
  sectionsConfig,
  mode,
  isAdmin = false,
  className,
  // CR151: Layout proof context
  layoutPresetName,
  layoutEnvironment,
  snapshotId,
}: DealDetailsContentProps) {
  // Sort sections by order and filter enabled ones
  const enabledSections = useMemo(() => {
    // CR170 DEBUG: Log what sections we're using
    console.log('[CR170-Render] DealDetailsContent received sectionsConfig:', 
      sectionsConfig.map(s => `${s.key}:${s.enabled}`).join(', '));
    return sectionsConfig
      .filter(s => s.enabled)
      .sort((a, b) => a.order - b.order);
  }, [sectionsConfig]);
  
  return (
    <div className={cn("space-y-4", className)}>
      {enabledSections.map((section, idx) => {
        const content = renderSection(section.key, deal, section, mode, isAdmin);
        if (!content) return null;
        
        return (
          <div key={section.key}>
            {idx > 0 && <Separator className="mb-4" />}
            {content}
          </div>
        );
      })}
      
      {enabledSections.length === 0 && (
        <p className="text-sm text-muted-foreground italic text-center py-4">
          No detail sections enabled
        </p>
      )}
      
      {/* CR332/CR333/CR479: Flyer Evidence section (admin-only, gated by deal type toggle) */}
      {isAdmin && ((deal as any).show_flyer_evidence ?? true) && (
        <>
          <Separator className="my-4" />
          <div
            id={`flyer-evidence-${(deal as any)?.id ?? 'unknown'}`}
            className="scroll-mt-4 transition-colors duration-700"
          >
            {(deal as any).flyer_artifact ? (
              <FlyerEvidenceSection flyerArtifact={(deal as any).flyer_artifact} />
            ) : (
              <FlyerPreview deal={deal} showDebugHint={isAdmin} />
            )}
          </div>
        </>
      )}
      
      {/* CR151: Admin-only proof section at the end */}
      {isAdmin && mode === "panel" && (
        <>
          <Separator className="my-4" />
          <DealProofSection
            deal={deal}
            layoutPresetName={layoutPresetName}
            layoutEnvironment={layoutEnvironment}
            snapshotId={snapshotId}
          />
        </>
      )}
    </div>
  );
}

export default DealDetailsContent;
